"use client"

import { useMemo, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { Checkbox, Badge } from "@medusajs/ui"
import { convertToLocale } from "@/lib/util/money"
import { addToCartBulk } from "@/lib/data/cart"
import type { EnrichedBundle } from "@/lib/data/product-bundles"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"

type BundleBuyTogetherProps = {
  bundles: EnrichedBundle[]
  countryCode: string
  /** Optional heading override (e.g. for cart page) */
  heading?: string
  /** Products already in the cart — pre-unchecked so we only upsell the rest */
  excludeProductIds?: string[]
}

const PlusDivider = () => (
  <div className="flex items-center justify-center shrink-0 px-3">
    <span className="text-neutral-300 text-xl font-light select-none">+</span>
  </div>
)

const AddButton = ({
  onClick,
  disabled,
  pending,
  added,
  label,
  addedLabel,
}: {
  onClick: () => void
  disabled: boolean
  pending: boolean
  added: boolean
  label: string
  addedLabel: string
}) => (
  <button
    onClick={onClick}
    disabled={disabled || pending}
    className={`
      inline-flex items-center justify-center gap-2 rounded-lg px-6 py-2.5
      text-sm font-semibold transition-all duration-150 whitespace-nowrap
      ${disabled
        ? "bg-neutral-100 text-neutral-400 cursor-not-allowed border border-neutral-200"
        : added
          ? "bg-emerald-600 text-white"
          : "bg-navy text-white hover:bg-navy/90 active:scale-[0.98]"
      }
    `}
  >
    {pending ? (
      <span className="animate-spin w-4 h-4 border-2 border-white/40 border-t-white rounded-full" />
    ) : added ? (
      addedLabel
    ) : (
      label
    )}
  </button>
)

function BundleCard({
  bundle,
  countryCode,
  excludeProductIds = [],
}: {
  bundle: EnrichedBundle
  countryCode: string
  excludeProductIds?: string[]
}) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [added, setAdded] = useState(false)
  const excluded = useMemo(
    () => new Set(excludeProductIds),
    [excludeProductIds]
  )

  const addable = useMemo(
    () => bundle.items.filter((i) => i.variantId),
    [bundle.items]
  )

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      addable.map((i) => [i.productId, i.inStock && !excluded.has(i.productId)])
    )
  )

  const isSimple = addable.length <= 2
  const selectedItems = isSimple
    ? addable.filter((i) => i.inStock && !excluded.has(i.productId))
    : addable.filter((i) => checked[i.productId])
  const total = selectedItems.reduce((sum, i) => sum + (i.amount ?? 0), 0)

  const toggle = (productId: string) =>
    setChecked((prev) => ({ ...prev, [productId]: !prev[productId] }))

  const handleAdd = () => {
    const lineItems = selectedItems
      .filter((i) => i.variantId && !excluded.has(i.productId))
      .map((i) => ({ variant_id: i.variantId as string, quantity: 1 }))

    if (!lineItems.length) return

    startTransition(async () => {
      try {
        await addToCartBulk({ lineItems, countryCode })
        setAdded(true)
        router.refresh()
        setTimeout(() => setAdded(false), 2500)
      } catch {
        // swallow
      }
    })
  }

  if (isSimple) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-white p-5 md:p-6">
        {bundle.subtitle && (
          <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
            {bundle.subtitle}
          </p>
        )}

        {/* Two products side by side — image on top, details below */}
        <div className="flex items-start gap-1 mb-5">
          {addable.map((item, idx) => (
            <div key={item.productId} className="flex items-start flex-1 min-w-0">
              {idx > 0 && (
                <div className="flex h-40 shrink-0 items-center px-3">
                  <span className="text-neutral-300 text-xl font-light select-none">+</span>
                </div>
              )}
              <LocalizedClientLink
                href={`/products/${item.handle}`}
                className="group flex flex-1 flex-col items-center gap-2 min-w-0 rounded-lg p-1 -m-1 transition-colors hover:bg-neutral-50"
              >
                <div className="flex h-40 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-neutral-100 bg-neutral-50 p-2">
                  {item.thumbnail && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="max-h-full max-w-full object-contain"
                    />
                  )}
                </div>
                <div className="flex w-full flex-col items-center gap-0.5 min-w-0 text-center">
                  <span className="text-sm text-neutral-800 font-medium line-clamp-2 leading-snug group-hover:text-navy">
                    {item.title}
                  </span>
                  <span className="text-sm font-semibold text-navy">
                    {item.amount != null
                      ? convertToLocale({ amount: item.amount, currency_code: bundle.currencyCode })
                      : "—"}
                  </span>
                  {!item.inStock && (
                    <Badge size="2xsmall" color="orange">Ni na zalogi</Badge>
                  )}
                </div>
              </LocalizedClientLink>
            </div>
          ))}
        </div>

        {/* Total + CTA */}
        <div className="flex items-center justify-between border-t border-neutral-100 pt-4 gap-4">
          <div className="flex items-baseline gap-2">
            <span className="text-xs text-neutral-400 uppercase tracking-wider">Skupaj</span>
            <span className="text-xl font-bold text-navy">
              {convertToLocale({ amount: total, currency_code: bundle.currencyCode })}
            </span>
          </div>
          <AddButton
            onClick={handleAdd}
            disabled={!selectedItems.length}
            pending={pending}
            added={added}
            label="Dodaj komplet"
            addedLabel="Dodano"
          />
        </div>
      </div>
    )
  }

  // Selection mode for 3+ items
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 md:p-6">
      {bundle.subtitle && (
        <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-4">
          {bundle.subtitle}
        </p>
      )}

      <div className="flex items-start gap-2 overflow-x-auto pb-2">
        {addable.map((item, idx) => (
          <div key={item.productId} className="flex items-start">
            {idx > 0 && <PlusDivider />}
            <label className={`
              flex w-32 shrink-0 cursor-pointer flex-col items-center gap-2 rounded-xl p-3
              border transition-all duration-150
              ${checked[item.productId]
                ? "border-navy/25 bg-white shadow-sm"
                : "border-neutral-200 bg-neutral-50 opacity-50"
              }
              hover:border-navy/35 hover:opacity-100
            `}>
              <div className="relative">
                <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-neutral-100 bg-white p-1.5">
                  {item.thumbnail && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="max-h-full max-w-full object-contain"
                    />
                  )}
                </div>
                <span className="absolute -left-1.5 -top-1.5 z-10">
                  <Checkbox
                    checked={!!checked[item.productId]}
                    onCheckedChange={() => toggle(item.productId)}
                  />
                </span>
                {!item.inStock && (
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                    <Badge size="2xsmall" color="orange">Ni na zalogi</Badge>
                  </span>
                )}
              </div>
              <span className="line-clamp-2 text-center text-xs leading-snug text-neutral-700 font-medium mt-1">
                {item.title}
              </span>
              <span className="text-sm font-semibold text-navy">
                {item.amount != null
                  ? convertToLocale({ amount: item.amount, currency_code: bundle.currencyCode })
                  : "—"}
              </span>
            </label>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-neutral-100 pt-4 gap-4">
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-neutral-400 uppercase tracking-wider">
            Skupaj ({selectedItems.length})
          </span>
          <span className="text-xl font-bold text-navy">
            {convertToLocale({ amount: total, currency_code: bundle.currencyCode })}
          </span>
        </div>
        <AddButton
          onClick={handleAdd}
          disabled={!selectedItems.length}
          pending={pending}
          added={added}
          label="Dodaj v košarico"
          addedLabel="Dodano"
        />
      </div>
    </div>
  )
}

export default function BundleBuyTogether({
  bundles,
  countryCode,
  heading = "Pogosto kupljeno skupaj",
  excludeProductIds = [],
}: BundleBuyTogetherProps) {
  if (!bundles?.length) return null

  const excluded = new Set(excludeProductIds)
  const visibleBundles = bundles.filter((bundle) => {
    const addable = bundle.items.filter((i) => i.variantId)
    return addable.some((i) => !excluded.has(i.productId))
  })

  if (!visibleBundles.length) return null

  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-xl font-semibold text-neutral-900">{heading}</h2>
      <div className="flex flex-col gap-4">
        {visibleBundles.map((bundle) => (
          <BundleCard
            key={bundle.id}
            bundle={bundle}
            countryCode={countryCode}
            excludeProductIds={excludeProductIds}
          />
        ))}
      </div>
    </div>
  )
}
